/**
 * WorkflowStartPage · Overwatch Runner landing (3-column shell)
 * ──────────────────────────────────────────────────────────────
 * Matches the visual design of `/workflow/appraisal/:id` (StatusPipelineTabs
 * at top · StatusRail on left · TaskTreeMiddle in middle · Right pane) but
 * without a picked appraisal. The right pane is the entry chooser + picker.
 *
 * Layout order in the RIGHT PANE (per user spec · iter303ai-fix5):
 *   1. New Client / Existing Client big cards       ← primary action
 *   2. Search picker + inline "+ New Client" link   ← work on an existing appraisal
 *   3. Resume in-progress runs                       ← quick jump
 *   4. Studio links (Split View · Edit templates)   ← utilities
 *
 * The Left rail + Top tabs default to Client Data. Clicking another status
 * fetches its raw workflow template and repaints the middle column as a
 * read-only outline (nothing is actionable until an appraisal is picked).
 *
 * NO auto-redirect (Bug 1 · already reverted). NO localStorage read.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserPlus, UserCheck, Loader2, Search } from 'lucide-react';

import { api, getWorkflow, STATUS_ORDER, STATUS_LABELS, useStatusLabels } from './pipelineApi';
import { useStages, stageLabel } from '@/lib/stages';
import StatusPipelineTabs from './components/StatusPipelineTabs';
import StatusRail          from './components/StatusRail';
import TaskTreeMiddle      from './components/TaskTreeMiddle';

// A ladder of 11 inert-state rows — feeds the top tabs + left rail on the
// landing page so the shell looks identical to the console.
function buildInertLadder(activeKey) {
  return STATUS_ORDER.map(key => ({
    status_key:      key,
    status_label:    STATUS_LABELS[key],
    state:           key === activeKey ? 'in_progress' : 'unstarted',
    tasks:           [],
    tasks_readonly: [],
  }));
}

// Overlay a fetched raw workflow's tasks onto a ladder row, marking every
// action as `unstarted` so TaskTreeMiddle can render a clean outline.
function overlayInertTasks(wf) {
  const walk = (actions, depth = 0) => {
    const out = [];
    for (const a of (actions || [])) {
      const params = a.params || {};
      out.push({
        id: a.id, path: [a.id], type: a.type,
        label: params.label || params.prompt || a.type,
        state: 'unstarted', sub_depth: depth,
      });
      if ((a.subs || []).length) out.push(...walk(a.subs, depth + 1));
    }
    return out;
  };
  return (wf.tasks || []).map(t => {
    const firstPath = (t.paths || [{}])[0];
    return {
      id: t.id, order: t.order,
      label: t.label, desc: t.desc,
      state: 'unstarted',
      actions: walk(firstPath.actions || []),
    };
  });
}

export default function WorkflowStartPage() {
  const stageLabels = useStatusLabels();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPickMode = searchParams.get('pick') === '1';

  const [activeKey, setActiveKey] = useState('client_data');
  const [tasksByStatus, setTasksByStatus] = useState({});   // status_key → inert-overlay tasks[]
  const [loadingTasks, setLoadingTasks]   = useState(false);
  const [starting, setStarting]           = useState(null);
  const [error, setError]                 = useState('');

  // Fetch the raw workflow for the active status on switch, cache locally
  useEffect(() => {
    if (tasksByStatus[activeKey]) return;   // already cached
    setLoadingTasks(true);
    getWorkflow(activeKey)
      .then(wf => setTasksByStatus(prev => ({ ...prev, [activeKey]: overlayInertTasks(wf) })))
      .catch(() => setTasksByStatus(prev => ({ ...prev, [activeKey]: [] })))
      .finally(() => setLoadingTasks(false));
  }, [activeKey, tasksByStatus]);

  const ladder = buildInertLadder(activeKey).map(r => (
    r.status_key === activeKey
      ? { ...r, tasks: tasksByStatus[activeKey] || [] }
      : r
  ));
  const activeRow = ladder.find(r => r.status_key === activeKey);

  const startRun = async (entry_choice) => {
    setStarting(entry_choice); setError('');
    try {
      const res = await api.post('/workflow-runs', { entry_choice });
      navigate(`/workflow/${res.data.id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setStarting(null);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-slate-50" data-testid="wf-console">
      <StatusPipelineTabs
        ladder={ladder}
        activeKey={activeKey}
        onPick={(k) => setActiveKey(k)}
      />

      {/* No appraisal header — replaced with a landing banner */}
      <div className="border-b border-slate-200 bg-white px-4 py-2 flex items-center gap-3">
        <div className="text-xs text-slate-500">
          <span className="uppercase tracking-widest text-[10px] text-slate-400 mr-1">Overwatch Runner</span>
          <span className="font-semibold text-slate-800">No appraisal selected</span>
        </div>
        <span className="text-slate-300">·</span>
        <div className="text-xs text-slate-500">
          Pick a status on the left to preview its tasks, or start a run from the right pane.
        </div>
      </div>

      {/* 3-column body */}
      <div className="flex-1 flex overflow-hidden">
        <StatusRail
          ladder={ladder}
          activeKey={activeKey}
          onPick={(k) => setActiveKey(k)}
        />
        <TaskTreeMiddle
          statusRow={activeRow}
          currentTaskId={null}
          activeActionId={null}
          onPickAction={() => { /* no-op — no run to advance */ }}
          onSwitchTask={undefined}
        />

        {/* Right pane · entry chooser + picker (SEE user-requested ORDER) */}
        <main className="flex-1 overflow-auto bg-white">
          <div className="max-w-3xl mx-auto p-8">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-2">
              Overwatch Runner · {stageLabels[activeKey]}
            </div>
            {activeKey === 'client_data' ? (
              <>
                <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="wf-right-title">
                  Pick a task or an existing appraisal
                </h1>
                <p className="text-slate-500 mb-6">
                  Start a fresh client entry, work on an existing appraisal, or resume an in-progress run.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="wf-right-title">
                  Pick an appraisal ready for {stageLabels[activeKey]}
                </h1>
                <p className="text-slate-500 mb-6">
                  Downstream statuses need an appraisal that has already progressed to this stage.
                  Pick one below to open the focus-mode console, or resume an in-progress run.
                </p>
              </>
            )}

            {error && (
              <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3 mb-6" data-testid="workflow-start-error">
                {error}
              </div>
            )}

            {/* PRIMARY · New / Existing Client big cards (Client Data only · hidden in ?pick=1 switch mode) */}
            {activeKey === 'client_data' && !isPickMode && (
              <div className="grid grid-cols-2 gap-4 mb-8" data-testid="workflow-start-fresh">
                <button onClick={() => startRun('new_client')}
                  data-testid="workflow-start-new"
                  disabled={starting !== null}
                  className="group p-5 bg-white border-2 border-slate-200 rounded-xl hover:border-sky-400 hover:shadow-md transition disabled:opacity-40 text-left">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-sky-100 text-sky-600 rounded-full group-hover:bg-sky-500 group-hover:text-white transition">
                      {starting === 'new_client' ? <Loader2 size={22} className="animate-spin" /> : <UserPlus size={22} />}
                    </div>
                    <div className="flex-1">
                      <div className="text-lg font-semibold text-slate-800">New Client</div>
                      <div className="text-xs text-slate-500">First-time caller — full details.</div>
                    </div>
                  </div>
                </button>

                <button onClick={() => startRun('existing_client')}
                  data-testid="workflow-start-existing"
                  disabled={starting !== null}
                  className="group p-5 bg-white border-2 border-slate-200 rounded-xl hover:border-emerald-400 hover:shadow-md transition disabled:opacity-40 text-left">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full group-hover:bg-emerald-500 group-hover:text-white transition">
                      {starting === 'existing_client' ? <Loader2 size={22} className="animate-spin" /> : <UserCheck size={22} />}
                    </div>
                    <div className="flex-1">
                      <div className="text-lg font-semibold text-slate-800">Existing Client</div>
                      <div className="text-xs text-slate-500">Look up records and confirm.</div>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* SECONDARY · Search + list of existing appraisals */}
            <div className="mb-2 text-xs uppercase tracking-widest text-slate-400 font-semibold">
              {activeKey === 'client_data'
                ? 'or work on an existing appraisal'
                : `appraisals currently at or ready for ${stageLabels[activeKey]}`}
            </div>
            <AppraisalPicker
              navigate={navigate}
              setError={setError}
              pipelineStatus={activeKey === 'client_data' ? '' : activeKey}
            />

            {/* Small inline "+ New Client" shortcut · Client Data only */}
            {activeKey === 'client_data' && (
              <div className="mt-4 flex items-center justify-between text-sm">
                <div className="text-slate-500">Client not on file yet?</div>
                <button
                  onClick={() => startRun('new_client')}
                  disabled={starting !== null}
                  data-testid="workflow-start-new-inline"
                  className="inline-flex items-center gap-1 text-sky-700 hover:text-sky-900 font-semibold hover:underline decoration-dotted">
                  {starting === 'new_client' ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />}
                  + New Client entry
                </button>
              </div>
            )}

            {/* Resume list */}
            <ResumeList navigate={navigate} filterStatusKey={activeKey === 'client_data' ? '' : activeKey} />

            {/* Studio + Split-view links */}
            <div className="mt-8 text-center space-x-4">
              <button onClick={() => navigate('/workflow/split')}
                data-testid="workflow-start-split-view"
                className="text-xs text-indigo-600 hover:text-indigo-800 underline decoration-dotted font-semibold">
                ⚏ Open Split View (Studio + Runner)
              </button>
              <button onClick={() => navigate('/settings/status-workflows')}
                data-testid="workflow-start-edit-templates"
                className="text-xs text-slate-500 hover:text-sky-600 underline decoration-dotted">
                Edit the workflow templates in the Studio →
              </button>
            </div>

            {loadingTasks && (
              <div className="mt-4 text-[11px] text-slate-400 flex items-center gap-2">
                <Loader2 size={11} className="animate-spin" /> Loading {stageLabels[activeKey]} task outline…
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}


/**
 * AppraisalPicker · live search + scrollable result list (≈5 rows visible).
 * Clicking a row opens the 3-column focus-mode console for that appraisal.
 *
 * iter303ai-fix7 — accepts an optional `pipelineStatus` prop. When set, the
 * search only returns appraisals whose pipeline_progress has that status as
 * `in_progress` or `unstarted` — so downstream status tabs can filter to
 * only the appraisals that can actually be worked on for that status.
 */
function AppraisalPicker({ navigate, setError, pipelineStatus }) {
  const STATUS_LABELS = useStatusLabels();
  const stages = useStages(api);
  const [query, setQuery] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const timer = React.useRef(null);

  React.useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: 20, search: query.trim() };
        if (pipelineStatus) params.pipeline_status = pipelineStatus;
        const res = await api.get('/appraisals', { params });
        setItems(res.data?.items || []);
      } catch (e) { setError(e?.response?.data?.detail || e.message); }
      finally { setLoading(false); }
    }, 220);
    return () => timer.current && clearTimeout(timer.current);
  }, [query, setError, pipelineStatus]);

  return (
    <div data-testid="wf-appraisal-picker">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-3 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={pipelineStatus
            ? `Search ${STATUS_LABELS[pipelineStatus]} appraisals — by number, client or address…`
            : "Search by appraisal no · client · property address…"}
          data-testid="wf-picker-search"
          className="w-full pl-10 pr-4 py-2.5 border-2 border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
        />
      </div>
      <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden bg-white shadow-sm">
        {loading && (
          <div className="p-4 text-xs text-slate-400 flex items-center gap-2">
            <Loader2 size={12} className="animate-spin" /> Searching…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="p-4 text-xs text-slate-400 italic" data-testid="wf-picker-empty">
            {pipelineStatus
              ? `No appraisals currently at or ready for ${STATUS_LABELS[pipelineStatus]}.`
              : 'No appraisals match.'}
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="max-h-[22rem] overflow-y-auto" data-testid="wf-picker-scroll">
            {items.map(a => (
              <button
                key={a._id}
                onClick={() => navigate(`/workflow/appraisal/${a._id}${pipelineStatus ? `?status=${pipelineStatus}` : ''}`)}
                data-testid={`wf-picker-item-${a._id}`}
                className="w-full text-left px-4 py-3 border-b last:border-b-0 border-slate-100 hover:bg-sky-50 flex items-center gap-3 transition"
              >
                <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold bg-slate-100 rounded px-2 py-0.5 w-16 text-center">
                  A{a.appraisal_no || '—'}
                </span>
                <span className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">
                    {a.client_name || '(no client)'}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {a.property_address || '(no address)'}
                  </div>
                </span>
                <span className="text-[10px] uppercase tracking-widest text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                  {a.appraisal_status === 'legacy_pending_review' ? 'Legacy' : (stageLabel(stages, a.appraisal_status) || 'unset')}
                </span>
                <span className="text-sky-600 text-xs font-semibold">Open →</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="mt-2 text-[11px] text-slate-400" data-testid="wf-picker-count">
        {items.length > 5
          ? `${items.length} appraisals · scroll for more`
          : `${items.length} appraisal${items.length === 1 ? '' : 's'}`}
      </div>
    </div>
  );
}


function ResumeList({ navigate, filterStatusKey }) {
  const STATUS_LABELS = useStatusLabels();
  const [runs, setRuns] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/workflow-runs', { params: { status: 'in_progress' } });
        setRuns(res.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  const visible = filterStatusKey ? runs.filter(r => r.status_key === filterStatusKey) : runs;
  if (!visible.length) return null;

  return (
    <div className="mt-8">
      <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3">
        Resume in-progress runs ({visible.length}{filterStatusKey ? ` · ${STATUS_LABELS[filterStatusKey]}` : ''})
      </div>
      <div className="space-y-2">
        {visible.map(r => {
          const target = r.appraisal_id
            ? `/workflow/appraisal/${r.appraisal_id}`
            : `/workflow/${r.id}`;
          return (
            <button key={r.id}
              data-testid={`workflow-resume-${r.id.slice(0, 8)}`}
              onClick={() => navigate(target)}
              className="w-full flex items-center justify-between p-3 bg-white border border-slate-200 rounded hover:border-sky-300 hover:bg-sky-50/40 text-left transition">
              <div>
                <div className="text-sm font-medium text-slate-700">
                  {r.entry_choice === 'new_client' ? 'New Client' : r.entry_choice === 'existing_client' ? 'Existing Client' : 'Seeded'} · Status: <span className="text-sky-700">{r.status_key}</span>
                </div>
                <div className="text-xs text-slate-500 font-mono">
                  {r.id.slice(0, 8)}… · started {r.started_at.slice(0, 16).replace('T', ' ')} · {(r.completed_actions || []).length} action(s) done
                </div>
              </div>
              <div className="text-xs text-sky-700 font-semibold">Resume →</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
